import { useEffect, useRef, type CSSProperties } from 'react'
import { boardThemeClass, useSettings } from '@/lib/settings'

export type Side = 'white' | 'black'
export type BoardPiece = { color: Side; kind: 'k' | 'q' | 'r' | 'b' | 'n' | 'p' }
export type BoardArrow = { from: string; to: string; color: string }

type BoardProps = {
  side?: Side
  pieces?: Record<string, BoardPiece>
  /** Whole-square fills, as CSS colours. */
  highlights?: Record<string, string>
  /** Rounded tiles inside squares, as CSS colours (the mastery map). */
  markers?: Record<string, string>
  /** Square outlines, as CSS colours. */
  rings?: Record<string, string>
  labels?: Record<string, string>
  arrows?: BoardArrow[]
  showCoordinates?: boolean
  /** Makes squares clickable (with a hover ring). */
  onSquareClick?: (square: string) => void
  className?: string
}

const FILES = 'abcdefgh'
const GLYPHS: Record<BoardPiece['kind'], [solid: string, outline: string]> = {
  k: ['♚', '♔'], q: ['♛', '♕'], r: ['♜', '♖'], b: ['♝', '♗'], n: ['♞', '♘'], p: ['♟', '♙'],
}

/** Square names from the top-left, as seen from White. The board is rotated as a whole for Black. */
const WHITE_LAYOUT = Array.from({ length: 64 }, (_, index) => `${FILES[index % 8]}${8 - Math.floor(index / 8)}`)

function isLight(square: string) {
  return (FILES.indexOf(square[0]) + Number(square[1])) % 2 === 0
}

export default function Board({
  side = 'white',
  pieces = {},
  highlights = {},
  markers = {},
  rings = {},
  labels = {},
  arrows = [],
  showCoordinates = false,
  onSquareClick,
  className = '',
}: BoardProps) {
  const { boardTheme } = useSettings()
  const grid = useRef<HTMLDivElement>(null)
  const previousSide = useRef(side)

  // Shrink mid-flip so the spinning board's corners stay inside its frame.
  useEffect(() => {
    if (previousSide.current !== side) {
      grid.current?.animate([{ scale: 1 }, { scale: 0.7 }, { scale: 1 }], { duration: 350, easing: 'ease-in-out' })
      previousSide.current = side
    }
  }, [side])

  const flipped = side === 'black'
  // Labels and pieces turn the other way so they stay upright while the board spins.
  const upright = `transition-[rotate] duration-350 ease-in-out ${flipped ? 'rotate-180' : ''}`

  return (
    <div className={`${boardThemeClass(boardTheme)} @container relative aspect-square w-full ${className}`}>
      <div
        ref={grid}
        className={`grid size-full grid-cols-8 grid-rows-8 overflow-hidden rounded-lg shadow-lg ring-1 ring-black/10 transition-[rotate] duration-350 ease-in-out dark:shadow-none dark:ring-white/10 ${flipped ? 'rotate-180' : ''}`}
      >
        {WHITE_LAYOUT.map((square) => {
          const piece = pieces[square]
          const Square = onSquareClick ? 'button' : 'div'
          return (
            <Square
              key={square}
              {...(onSquareClick ? { type: 'button' as const, onClick: () => onSquareClick(square), 'aria-label': square } : {})}
              className={`relative grid place-items-center ${isLight(square) ? 'bg-(--board-light)' : 'bg-(--board-dark)'} ${onSquareClick ? 'cursor-pointer hover:inset-ring-[0.6cqi] hover:inset-ring-white/85' : ''}`}
            >
              {highlights[square] && (
                <div className="absolute inset-0 bg-(--fill)" style={{ '--fill': highlights[square] } as CSSProperties} />
              )}
              {markers[square] && (
                <div
                  className="absolute inset-[1.75cqi] rounded-[1.5cqi] bg-(--fill)"
                  style={{ '--fill': markers[square] } as CSSProperties}
                />
              )}
              {rings[square] && (
                <div
                  className="absolute inset-0 border-[1cqi] border-(--ring)"
                  style={{ '--ring': rings[square] } as CSSProperties}
                />
              )}
              {piece && (
                <div className={`relative grid font-chess text-[10.5cqi] leading-none select-none ${upright}`}>
                  <div className={`col-start-1 row-start-1 ${piece.color === 'white' ? 'text-white' : 'text-zinc-950'}`}>
                    {GLYPHS[piece.kind][0]}&#xFE0E;
                  </div>
                  {piece.color === 'white' && (
                    <div className="col-start-1 row-start-1 text-zinc-950">{GLYPHS[piece.kind][1]}&#xFE0E;</div>
                  )}
                </div>
              )}
              {labels[square] && (
                <div
                  className={`relative text-[4.5cqi] font-bold select-none ${upright} ${highlights[square] || !isLight(square) ? 'text-white [text-shadow:0_1px_3px_rgb(0_0_0/0.5)]' : 'text-zinc-950/80'}`}
                >
                  {labels[square]}
                </div>
              )}
            </Square>
          )
        })}
        {arrows.length > 0 && (
          <svg viewBox="0 0 8 8" className="pointer-events-none absolute inset-0 size-full" aria-hidden="true">
            {arrows.map((arrow, index) => (
              <polygon key={index} points={arrowPoints(arrow.from, arrow.to)} fill={arrow.color} opacity="0.85" />
            ))}
          </svg>
        )}
      </div>
      {showCoordinates && <Coordinates side={side} />}
    </div>
  )
}

/** Rank numbers down the left edge and file letters along the bottom, placed for the side being shown. */
function Coordinates({ side }: { side: Side }) {
  const files = side === 'white' ? FILES.split('') : FILES.split('').reverse()
  const ranks = side === 'white' ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8]
  return (
    <div className="pointer-events-none absolute inset-0 grid grid-cols-8 grid-rows-8 text-[2.6cqi] font-semibold select-none" aria-hidden="true">
      {ranks.map((rank, row) => (
        <div
          key={`r${rank}`}
          className={`col-start-1 p-[0.6cqi] ${isLight(`${files[0]}${rank}`) ? 'text-(--board-dark)' : 'text-(--board-light)'}`}
          style={{ gridRowStart: row + 1 }}
        >
          {rank}
        </div>
      ))}
      {files.map((file, column) => (
        <div
          key={`f${file}`}
          className={`row-start-8 self-end justify-self-end p-[0.6cqi] ${isLight(`${file}${ranks[7]}`) ? 'text-(--board-dark)' : 'text-(--board-light)'}`}
          style={{ gridColumnStart: column + 1 }}
        >
          {file}
        </div>
      ))}
    </div>
  )
}

/** Arrow outline between two square centres in the board's 8×8 viewBox (White's layout). */
function arrowPoints(from: string, to: string, width = 0.18) {
  const center = (square: string) => [FILES.indexOf(square[0]) + 0.5, 8 - Number(square[1]) + 0.5]
  const [x1, y1] = center(from)
  const [x2, y2] = center(to)
  const length = Math.hypot(x2 - x1, y2 - y1)
  const [dx, dy] = [(x2 - x1) / length, (y2 - y1) / length]
  const [nx, ny] = [-dy, dx]
  const headLength = Math.min(width * 2.2, length * 0.6)
  const headWidth = width * 2.4
  const [neckX, neckY] = [x2 - dx * headLength, y2 - dy * headLength]
  const point = (x: number, y: number, offset: number) => `${x + nx * offset},${y + ny * offset}`
  return [
    point(x1, y1, width / 2), point(neckX, neckY, width / 2), point(neckX, neckY, headWidth / 2), `${x2},${y2}`,
    point(neckX, neckY, -headWidth / 2), point(neckX, neckY, -width / 2), point(x1, y1, -width / 2),
  ].join(' ')
}
